import { useState, useEffect } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '../config/firebase';
import type { CurrentUser, User, University } from '../types';
import { loginUser, logoutUser } from '../services/authService';
import { getUserByFirebaseUid } from '../services/userService';
import { config } from '../config/env';

interface UseAuthReturn {
  currentUser: CurrentUser | null;
  authLoading: boolean;
  loginMode: boolean;
  selectedUniversity: string;
  email: string;
  password: string;
  error: string;
  setLoginMode: (mode: boolean) => void;
  setSelectedUniversity: (id: string) => void;
  setEmail: (email: string) => void;
  setPassword: (password: string) => void;
  handleLogin: (universities: University[], users: User[]) => Promise<void>;
  handleLogout: () => Promise<void>;
}

export const useAuth = (): UseAuthReturn => {
  const [currentUser, setCurrentUser] = useState<CurrentUser | null>(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [loginMode, setLoginMode] = useState(true);
  const [selectedUniversity, setSelectedUniversity] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');

  // Restore session on page load via Firebase auth persistence
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        try {
          const user = await getUserByFirebaseUid(firebaseUser.uid);
          if (user && user.status === 'active') {
            setCurrentUser({
              id: user.id,
              name: user.name,
              email: user.email,
              role: user.role,
              universityId: user.universityId,
            });
          }
        } catch {
          // User record not found in Firestore — stay logged out
        }
      }
      setAuthLoading(false);
    });
    return unsubscribe;
  }, []);

  const handleLogin = async (universities: University[], users: User[]) => {
    setError('');

    if (email === config.admin.email) {
      if (password !== config.admin.password) {
        setError('Incorrect admin password. Please try again.');
        return;
      }
      setCurrentUser({
        id: 'admin',
        name: config.admin.name,
        email: config.admin.email,
        role: 'super_admin',
        universityId: null,
      });
      return;
    }

    if (!selectedUniversity || !email || !password) {
      setError('Please fill in all required fields.');
      return;
    }

    const university = universities.find(u => u.id === selectedUniversity);
    if (!university) {
      setError('Selected university not found.');
      return;
    }

    const user = users.find(
      u => u.email === email && u.universityId === selectedUniversity && u.status === 'active'
    );

    if (!user) {
      setError('User not found or account is inactive. Please contact your administrator.');
      return;
    }

    try {
      await loginUser(email, password);
      setCurrentUser({
        id: user.id,
        name: user.name,
        email: user.email,
        role: user.role,
        universityId: selectedUniversity,
      });
    } catch (authError: any) {
      if (authError.code === 'auth/user-not-found') {
        setError('User account not found. Please contact your administrator.');
      } else if (authError.code === 'auth/wrong-password') {
        setError('Incorrect password. Please try again.');
      } else if (authError.code === 'auth/too-many-requests') {
        setError('Too many failed attempts. Please try again later.');
      } else {
        setError('Authentication failed. Please check your credentials.');
      }
    }
  };

  const handleLogout = async () => {
    try {
      await logoutUser();
    } catch {
      // Logout errors are non-critical
    }
    setCurrentUser(null);
    setEmail('');
    setPassword('');
    setSelectedUniversity('');
    setError('');
  };

  return {
    currentUser,
    authLoading,
    loginMode,
    selectedUniversity,
    email,
    password,
    error,
    setLoginMode,
    setSelectedUniversity,
    setEmail,
    setPassword,
    handleLogin,
    handleLogout,
  };
};
